import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveReturnPath } from '../lib/navigation';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, LogIn, Mail } from 'lucide-react';

const Login: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const returnPath = React.useMemo(
    () => resolveReturnPath(location, searchParams),
    [location, searchParams],
  );

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, navigate, returnPath]);

  React.useEffect(() => {
    const state = location.state as { passwordResetSuccess?: boolean } | null;
    if (state?.passwordResetSuccess) {
      setBanner(t('passwordResetSuccess'));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-3xl">{t('signIn')}</CardTitle>
          <CardDescription className="text-center">{t('signInToAccount')}</CardDescription>
        </CardHeader>
        <CardContent>
          {banner && (
            <Alert variant="success" className="mb-4">
              {banner}
            </Alert>
          )}
          {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="email">{t('emailAddress')}</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input id="email" type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="password">{t('password')}</Label>
                <RouterLink to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
                  {t('forgotPassword')}
                </RouterLink>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input id="password" type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-12" />
                <button type="button" className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              <LogIn className="h-4 w-4" />
              {isLoading ? t('signingIn') : t('signIn')}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <RouterLink to="/register" className="text-primary hover:underline">
                {t('dontHaveAccount')} {t('signUp')}
              </RouterLink>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;

